import type { ComputedRef, Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import type {
  Geofence,
  GeoJsonFeatureCollection,
  LatLngBounds,
  MapMarker,
  ResolvedPlace,
  RoutePlan,
} from '@gpx-spa/geo-domain'
import { geofenceOutlineLatLngs, geofenceToFeature } from '@gpx-spa/geo-domain'
import { shipmentStopRoleFromIndex } from '@gpx-spa/constants'
import type { MapProvider } from '@gpx-spa/geo-domain'
import type { LineBufferGeometryCache } from '@gpx-spa/geo-adapters'
import {
  lineStringFeatureCollection,
  polygonFeatureCollection,
} from '@gpx-spa/geo-adapters'
import { yieldAnimationFrame, yieldUntilSpinnerPainted } from '@gpx-spa/ui'
import { pathIdForSegment } from '@/utils/device-api-shipment-payload'
import {
  alternativePathDashPattern,
  COMPOSER_DEBOUNCE,
  COMPOSER_LAYER_IDS,
  COMPOSER_MAP_STYLES,
  isFormPrimaryPath,
} from './constants'
import { ensureCorridorBuffersForRoutePlan } from './corridor-geometry'
import { boundsForStopGeofenceOrRadius, boundsFromLngLats, circleStopFeature } from './map-helpers'
import { rawCoordinatesForFormPath } from './path-vertex-stride'
import type { PaintMapTimings, FormMiddleStopPaint, FitBoundsMode } from './map-paint-types'
import {
  formMiddleSliceOnlyRadiusChangeWithGeofence,
  findFocusStopIndexFromPaintChange,
  isRenderableMiddleStopPaint,
  middleStopsDenseFromForm,
  stopsPaintChanged,
} from './stop-focus'
import {
  resolveFitModeForIntent,
  type MapCameraIntent,
} from './camera-intent'
import { V2_CURRENT_POSITION_MARKER_STYLE } from '@/map/operational/v2-current-positions-markers'
import type { LatLng } from '@/components/shipment/form/utils/build-routing-coordinates'

export type { FormMiddleStopPaint, FitBoundsMode } from './map-paint-types'

export type PaintMapOptions = {
  /**
   * When false, skip map busy overlay and spinner yield (fast path: radius/corridor/quality/toggles).
   * Default true.
   */
  showMapOverlay?: boolean
  /** Global stop index (0 = origin, last = destination, between = waypoints) when `fitMode` is `focus-stop`. */
  focusStopIndex?: number
}

export type UseShipmentFormMapPaintOptions = {
  mapProvider: MapProvider
  mapReady: Ref<boolean>
  routePlan: Ref<RoutePlan | null>
  selectedByPathId: Ref<Record<string, boolean>>
  corridorBufferMDebounced: Ref<number>
  geometryQualityDebounced: Ref<number>
  startResolved: Ref<ResolvedPlace | null | undefined> | ComputedRef<ResolvedPlace | null | undefined>
  endResolved: Ref<ResolvedPlace | null | undefined> | ComputedRef<ResolvedPlace | null | undefined>
  /** When set, the start stop layer uses {@link geofenceToFeature} instead of the address-radius circle. */
  startGeofence?: Ref<Geofence | null> | ComputedRef<Geofence | null>
  endGeofence?: Ref<Geofence | null> | ComputedRef<Geofence | null>
  radiusStartM: Ref<number>
  radiusEndM: Ref<number>
  /**
   * One slot per shipment stop (aligned with `stops[i]`): paint model for map + fit bounds.
   * Null when that stop has no coordinates/geofence yet.
   */
  formStopsPaint: Ref<(FormMiddleStopPaint | null)[]> | ComputedRef<(FormMiddleStopPaint | null)[]>
  pathUi: ComputedRef<Record<string, { color: string; label: string }>>
  computingRoute: Ref<boolean>
  routingLoading: Ref<boolean>
  geoCache: LineBufferGeometryCache
  trackerPositions?: Ref<LatLng[]> | ComputedRef<LatLng[]>
  /** When false, layers repaint but the camera stays put (edit hydrate bootstrap). */
  cameraEnabled?: Ref<boolean> | ComputedRef<boolean>
  /** Edit: defer stop focus until the first bootstrap overview fitBounds. */
  mapBootstrapMode?: Ref<boolean> | ComputedRef<boolean>
}

/** Numbered pins (1…n) for route preview — fills match stop cards ({@link SHIPMENT_STOP_ROLE_VISUAL} / Tailwind green-500, blue-500, slate-700). */
function buildFormStopNumberMarkers(
  paintArr: (FormMiddleStopPaint | null)[]
): MapMarker[] {
  const total = paintArr.length
  if (total === 0) return []
  const out: MapMarker[] = []
  for (let i = 0; i < total; i++) {
    const p = paintArr[i]
    if (!isRenderableMiddleStopPaint(p)) continue
    const role = shipmentStopRoleFromIndex(i, total)
    const fill =
      role === 'origin' ? '#22c55e' : role === 'waypoint' ? '#3b82f6' : '#334155'
    const { lat, lng } = p!.coordinates
    out.push({
      id: `composer-stop-number-${i}`,
      position: { lat, lng },
      style: {
        kind: 'circle',
        fillColor: fill,
        borderColor: '#ffffff',
        label: String(i + 1),
        sizePx: 32,
        fontSizePx: 14,
        textColor: '#ffffff',
      },
    })
  }
  return out
}

export function useShipmentFormMapPaint(options: UseShipmentFormMapPaintOptions) {
  const {
    mapProvider,
    mapReady,
    routePlan,
    selectedByPathId,
    corridorBufferMDebounced,
    geometryQualityDebounced,
    startResolved,
    endResolved,
    startGeofence,
    endGeofence,
    radiusStartM,
    radiusEndM,
    formStopsPaint,
    pathUi,
    routingLoading,
    geoCache,
    trackerPositions,
    cameraEnabled: cameraEnabledOption,
    mapBootstrapMode: mapBootstrapModeOption,
  } = options

  const cameraEnabled = computed(() => cameraEnabledOption?.value ?? true)
  const mapBootstrapMode = computed(() => mapBootstrapModeOption?.value ?? false)

  let bootstrapComplete = !mapBootstrapMode.value

  let pendingFocusStopIndex: number | null = null

  function resolveFitMode(fitMode: FitBoundsMode): FitBoundsMode {
    return resolveFitModeForIntent(fitMode, {
      mapReady: mapReady.value,
      bootstrapComplete,
      mapBootstrapMode: mapBootstrapMode.value,
      cameraEnabled: cameraEnabled.value,
    })
  }

  const isMapPainting = ref(false)
  const lastMiddleWaypointLayerCount = ref(0)

  const mapWorkBusy = computed(() => routingLoading.value || isMapPainting.value)

  let lastPaintedAlternativePathIds = new Set<string>()

  /** Serializes map paints so async corridor work cannot interleave and redraw deselected alternatives. */
  let paintMapChain: Promise<void> = Promise.resolve()

  /** Serializes camera intents so focus / overview cannot interleave. */
  let cameraIntentChain: Promise<void> = Promise.resolve()

  async function paintFullOverviewWithRetry(): Promise<boolean> {
    for (let attempt = 0; attempt < COMPOSER_DEBOUNCE.mapOverviewFitMaxAttempts; attempt++) {
      await paintMap(false, { showMapOverlay: false })
      mapProvider.notifyContainerResize()
      await yieldAnimationFrame()
      const timings = await paintMap('full', { showMapOverlay: false })
      if (timings != null && timings.fitBoundsMs > 0) return true
      await yieldAnimationFrame()
    }
    return false
  }

  async function applyCameraIntent(intent: MapCameraIntent): Promise<void> {
    await paintMapChain

    switch (intent.kind) {
      case 'focusStop':
        if (!bootstrapComplete) return
        if (!mapReady.value) {
          pendingFocusStopIndex = intent.index
          return
        }
        pendingFocusStopIndex = null
        await paintMap('focus-stop', {
          focusStopIndex: intent.index,
          showMapOverlay: false,
        })
        break
      case 'bootstrapOverview':
        if (!cameraEnabled.value) {
          await paintMap(false, { showMapOverlay: false })
          return
        }
        await paintFullOverviewWithRetry()
        bootstrapComplete = true
        break
      case 'routeOverview':
        if (mapBootstrapMode.value && !cameraEnabled.value) {
          await paintMap(false, { showMapOverlay: false })
          return
        }
        await paintFullOverviewWithRetry()
        break
    }
  }

  function requestRouteOverview(): Promise<void> {
    const run = applyCameraIntent({ kind: 'routeOverview' })
    cameraIntentChain = run.then(() => {}).catch(() => {})
    return run
  }

  function requestFocusStop(index: number) {
    cameraIntentChain = cameraIntentChain
      .then(() => applyCameraIntent({ kind: 'focusStop', index }))
      .catch(() => {})
  }

  function requestBootstrapOverview() {
    cameraIntentChain = cameraIntentChain
      .then(() => applyCameraIntent({ kind: 'bootstrapOverview' }))
      .catch(() => {})
  }

  function removePathLayersForPid(pid: string) {
    mapProvider.removeLayer(COMPOSER_LAYER_IDS.buffer(pid))
    mapProvider.removeLayer(COMPOSER_LAYER_IDS.path(pid))
  }

  async function paintMapSync(
    fitMode: FitBoundsMode,
    paintOptions?: PaintMapOptions
  ): Promise<PaintMapTimings | null> {
    fitMode = resolveFitMode(fitMode)
    const perf0 = performance.now()
    let msLeafletGeoJson = 0

    if (!mapReady.value) return null

    function collectDesiredPathIds(): Set<string> {
      const next = new Set<string>()
      const segments = routePlan.value?.segments ?? []
      for (let s = 0; s < segments.length; s++) {
        const paths = segments[s]?.paths ?? []
        for (let i = 0; i < paths.length; i++) {
          const p = paths[i]
          const pid = pathIdForSegment(s, p, i)
          if (!selectedByPathId.value[pid]) continue
          const raw = rawCoordinatesForFormPath(p)
          if (!raw || raw.length < 2) continue
          next.add(pid)
        }
      }
      return next
    }

    function removeLayersNotInDesired(desired: Set<string>) {
      for (const pid of [...lastPaintedAlternativePathIds]) {
        if (!desired.has(pid)) {
          removePathLayersForPid(pid)
          lastPaintedAlternativePathIds.delete(pid)
        }
      }
    }

    let desiredPathIds = collectDesiredPathIds()
    removeLayersNotInDesired(desiredPathIds)

    const points: Array<{ lat: number; lng: number }> = []

    const startGf = startGeofence?.value ?? null
    if (startGf) {
      for (const p of geofenceOutlineLatLngs(startGf)) {
        points.push(p)
      }
      const f = geofenceToFeature(startGf, { map: { ...COMPOSER_MAP_STYLES.stopStart } })
      if (f) {
        const fc: GeoJsonFeatureCollection = { type: 'FeatureCollection', features: [f] }
        mapProvider.addLayer(COMPOSER_LAYER_IDS.stopStart).setGeoJSON(fc)
      } else {
        mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopStart)
      }
    } else if (startResolved.value?.coordinates) {
      const { lat, lng } = startResolved.value.coordinates
      points.push({ lat, lng })
      const fc: GeoJsonFeatureCollection = {
        type: 'FeatureCollection',
        features: [
          circleStopFeature(lat, lng, radiusStartM.value, {
            ...COMPOSER_MAP_STYLES.stopStart,
          }),
        ],
      }
      mapProvider.addLayer(COMPOSER_LAYER_IDS.stopStart).setGeoJSON(fc)
    } else {
      mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopStart)
    }

    const endGf = endGeofence?.value ?? null
    if (endGf) {
      for (const p of geofenceOutlineLatLngs(endGf)) {
        points.push(p)
      }
      const f = geofenceToFeature(endGf, { map: { ...COMPOSER_MAP_STYLES.stopEnd } })
      if (f) {
        const fc: GeoJsonFeatureCollection = { type: 'FeatureCollection', features: [f] }
        mapProvider.addLayer(COMPOSER_LAYER_IDS.stopEnd).setGeoJSON(fc)
      } else {
        mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopEnd)
      }
    } else if (endResolved.value?.coordinates) {
      const { lat, lng } = endResolved.value.coordinates
      points.push({ lat, lng })
      const fc: GeoJsonFeatureCollection = {
        type: 'FeatureCollection',
        features: [
          circleStopFeature(lat, lng, radiusEndM.value, {
            ...COMPOSER_MAP_STYLES.stopEnd,
          }),
        ],
      }
      mapProvider.addLayer(COMPOSER_LAYER_IDS.stopEnd).setGeoJSON(fc)
    } else {
      mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopEnd)
    }

    const middles = middleStopsDenseFromForm(formStopsPaint.value)
    for (let i = middles.length; i < lastMiddleWaypointLayerCount.value; i++) {
      mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopWaypoint(i))
    }
    lastMiddleWaypointLayerCount.value = middles.length
    for (let i = 0; i < middles.length; i++) {
      const m = middles[i]
      const { lat, lng } = m.coordinates
      const wGf = m.geofence ?? null
      if (wGf) {
        for (const p of geofenceOutlineLatLngs(wGf)) {
          points.push(p)
        }
        const f = geofenceToFeature(wGf, { map: { ...COMPOSER_MAP_STYLES.stopWaypoint } })
        if (f) {
          const fc: GeoJsonFeatureCollection = { type: 'FeatureCollection', features: [f] }
          mapProvider.addLayer(COMPOSER_LAYER_IDS.stopWaypoint(i)).setGeoJSON(fc)
        } else {
          mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopWaypoint(i))
        }
      } else {
        points.push({ lat, lng })
        const fc: GeoJsonFeatureCollection = {
          type: 'FeatureCollection',
          features: [
            circleStopFeature(lat, lng, m.radiusM, {
              ...COMPOSER_MAP_STYLES.stopWaypoint,
            }),
          ],
        }
        mapProvider.addLayer(COMPOSER_LAYER_IDS.stopWaypoint(i)).setGeoJSON(fc)
      }
    }

    const setupAndStopsMs = performance.now() - perf0

    await yieldAnimationFrame()

    const bufM = corridorBufferMDebounced.value
    const q = geometryQualityDebounced.value

    desiredPathIds = collectDesiredPathIds()
    removeLayersNotInDesired(desiredPathIds)

    let msTurfBuffers = 0
    if (routePlan.value) {
      const t0 = performance.now()
      await ensureCorridorBuffersForRoutePlan(
        routePlan.value,
        selectedByPathId.value,
        geoCache,
        bufM,
        q
      )
      msTurfBuffers = performance.now() - t0
    }

    await yieldAnimationFrame()

    desiredPathIds = collectDesiredPathIds()
    removeLayersNotInDesired(desiredPathIds)

    const segments = routePlan.value?.segments ?? []
    for (let s = 0; s < segments.length; s++) {
      const paths = segments[s]?.paths ?? []
      for (let i = paths.length - 1; i >= 0; i--) {
        const p = paths[i]
        const pid = pathIdForSegment(s, p, i)
        if (!selectedByPathId.value[pid]) continue
        const raw = rawCoordinatesForFormPath(p)
        if (!raw || raw.length < 2) continue
        const coords = geoCache.getSimplified(pid, raw, q)
        const color = pathUi.value[pid]?.color ?? '#333333'
        const pathLineStyle = isFormPrimaryPath(p, i)
          ? COMPOSER_MAP_STYLES.pathLine(color)
          : COMPOSER_MAP_STYLES.pathLine(color, {
              lineDashArray: alternativePathDashPattern(pid),
            })

        const bufferLayerId = COMPOSER_LAYER_IDS.buffer(pid)
        const oneBuf = geoCache.getBufferedPolygon(pid, coords, bufM, q)
        if (oneBuf?.geometry) {
          const bufferFc = polygonFeatureCollection(
            oneBuf.geometry as GeoJsonFeatureCollection['features'][0]['geometry'],
            COMPOSER_MAP_STYLES.buffer(color)
          )
          const tL1 = performance.now()
          mapProvider.addLayer(bufferLayerId).setGeoJSON(bufferFc)
          msLeafletGeoJson += performance.now() - tL1
        } else {
          mapProvider.removeLayer(bufferLayerId)
        }

        const lineFc = lineStringFeatureCollection(coords, pathLineStyle)
        const pathLayerId = COMPOSER_LAYER_IDS.path(pid)
        const tL0 = performance.now()
        mapProvider.addLayer(pathLayerId).setGeoJSON(lineFc)
        msLeafletGeoJson += performance.now() - tL0

        for (const [lng, lat] of coords) {
          points.push({ lat, lng })
        }
      }
    }

    const stopNumberMarkers = buildFormStopNumberMarkers(formStopsPaint.value)
    if (stopNumberMarkers.length > 0) {
      mapProvider.addLayer(COMPOSER_LAYER_IDS.stopNumberPins).setMarkers(stopNumberMarkers)
    } else {
      mapProvider.removeLayer(COMPOSER_LAYER_IDS.stopNumberPins)
    }

    const trackers = (trackerPositions?.value ?? []).filter(
      (t) => Number.isFinite(t.lat) && Number.isFinite(t.lng),
    )
    if (trackers.length > 0) {
      for (const tracker of trackers) {
        points.push({ lat: tracker.lat, lng: tracker.lng })
      }
      mapProvider.addLayer(COMPOSER_LAYER_IDS.devicePositions).setMarkers(
        trackers.map((tracker, index) => ({
          id: `composer-tracker-${index}`,
          position: { lat: tracker.lat, lng: tracker.lng },
          style: V2_CURRENT_POSITION_MARKER_STYLE,
        })),
      )
    } else {
      mapProvider.removeLayer(COMPOSER_LAYER_IDS.devicePositions)
    }

    lastPaintedAlternativePathIds = new Set(collectDesiredPathIds())

    await yieldAnimationFrame()

    const b = boundsFromLngLats(points)

    function targetBoundsForFitMode(): LatLngBounds | null {
      if (fitMode === false) return null
      switch (fitMode) {
        case 'full':
          return b
        case 'focus-stop': {
          const idx = paintOptions?.focusStopIndex
          const arr = formStopsPaint.value
          if (idx == null || idx < 0 || idx >= arr.length) return null
          const m = arr[idx]
          if (!m) return null
          return boundsForStopGeofenceOrRadius(
            m.geofence ?? null,
            isRenderableMiddleStopPaint(m) ? m.coordinates : undefined,
            m.radiusM
          )
        }
        default: {
          const _exhaustive: never = fitMode
          return _exhaustive
        }
      }
    }

    let msFit = 0
    const fitTarget = targetBoundsForFitMode()
    if (fitTarget) {
      const tF0 = performance.now()
      mapProvider.fitBounds(fitTarget)
      msFit = performance.now() - tF0
    }

    const totalMs = performance.now() - perf0
    const timings: PaintMapTimings = {
      totalMs,
      setupAndStopsMs,
      turfBuffersMs: msTurfBuffers,
      leafletGeoJsonMs: msLeafletGeoJson,
      fitBoundsMs: msFit,
    }

    return timings
  }

  async function paintMap(
    fitMode: FitBoundsMode = false,
    paintOptions?: PaintMapOptions
  ): Promise<PaintMapTimings | null> {
    const showMapOverlay = paintOptions?.showMapOverlay !== false
    const run = async (): Promise<PaintMapTimings | null> => {
      if (showMapOverlay) {
        isMapPainting.value = true
        await yieldUntilSpinnerPainted()
      }
      try {
        return await paintMapSync(fitMode, paintOptions)
      } finally {
        if (showMapOverlay) {
          isMapPainting.value = false
        }
      }
    }
    const p = paintMapChain.then(run)
    paintMapChain = p.then(() => {}).catch(() => {})
    return p
  }

  function onMapReady() {
    mapReady.value = true
  }

  watch(mapReady, (ready) => {
    if (!ready || pendingFocusStopIndex == null || !bootstrapComplete) return
    requestFocusStop(pendingFocusStopIndex)
  })

  watch(
    [cameraEnabled, mapReady, mapBootstrapMode],
    ([sceneReady, ready, bootstrapMode], [prevSceneReady]) => {
      if (bootstrapMode && prevSceneReady && !sceneReady) {
        bootstrapComplete = false
      }
      if (!ready) return
      if (!sceneReady) {
        void paintMap(false, { showMapOverlay: false })
        return
      }
      if (bootstrapMode && !bootstrapComplete) {
        requestBootstrapOverview()
        return
      }
      void paintMap(false, { showMapOverlay: false })
    },
    { immediate: true },
  )

  watch(
    () =>
      (routePlan.value?.segments ?? []).reduce(
        (sum, segment) => sum + (segment.paths?.length ?? 0),
        0,
      ),
    (pathCount) => {
      if (pathCount === 0 && mapReady.value) {
        void paintMap(false, { showMapOverlay: false })
      }
    },
  )

  watch(
    () => selectedByPathId.value,
    () => {
      void paintMap(false, { showMapOverlay: false })
    },
  )

  watch(radiusStartM, () => {
    if (startGeofence?.value) return
    if (!startResolved.value?.coordinates) return
    void paintMap(false, { showMapOverlay: false })
  })

  watch(radiusEndM, () => {
    if (endGeofence?.value) return
    if (!endResolved.value?.coordinates) return
    void paintMap(false, { showMapOverlay: false })
  })

  watch(
    formStopsPaint,
    (newArr, oldArr) => {
      if (oldArr === undefined) {
        if (mapReady.value) {
          void paintMap(false, { showMapOverlay: false })
        }
        return
      }
      if (!stopsPaintChanged(newArr, oldArr)) return
      if (formMiddleSliceOnlyRadiusChangeWithGeofence(newArr, oldArr)) return

      const focusStopIndex = findFocusStopIndexFromPaintChange(newArr, oldArr)
      if (focusStopIndex !== undefined && bootstrapComplete) {
        requestFocusStop(focusStopIndex)
        return
      }
      if (!mapReady.value) return
      void paintMap(false, { showMapOverlay: false })
    },
    { deep: true },
  )

  watch(corridorBufferMDebounced, () => void paintMap(false, { showMapOverlay: false }))

  watch(geometryQualityDebounced, () => void paintMap(false, { showMapOverlay: false }))

  if (trackerPositions) {
    watch(
      trackerPositions,
      () => {
        if (!mapReady.value) return
        void paintMap(false, { showMapOverlay: false })
      },
      { deep: true },
    )
  }

  return {
    isMapPainting,
    mapWorkBusy,
    paintMap,
    requestRouteOverview,
    onMapReady,
  }
}
